import { useCallback, useEffect, useRef, useState } from 'react';

import { orders as ordersApi } from '../api/endpoints.ts';
import type { Order, OrderItem, OrderListItem, OrderRunPlace, OrderTracking } from '../api/types.ts';
import { formatPara, formatRsd, para } from '../core/money.ts';
import { totalNutrition } from '../core/nutrition.ts';
import { useStore } from '../core/store.ts';
import { OrderStatusGroup } from '../core/order-status.ts';
import { arrivalText, errorText, localized, relativeDay, statusText, useLang, useStrings } from '../i18n/index.ts';
import type { Strings } from '../i18n/strings.ts';
import { defaultMethod, goToPaymentPage, paymentConfigStore, payWithNewCard, payWithSavedCard, savedCardsStore, type CardInput, type PaymentMethod } from '../state/payment.ts';
import { BankBadge } from '../ui/BankBadge.tsx';
import { CardForm } from '../ui/CardForm.tsx';
import { PaymentPicker } from '../ui/PaymentPicker.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { lookupProduct } from '../state/reorder.ts';
import { pushRoute } from '../state/router.ts';
import { notify } from '../state/toasts.ts';
import { useWatcher } from '../state/watcher.ts';
import { Icon, type IconName } from '../ui/icons.tsx';
import { Button, Card, cx, ErrorBanner, RemoteImage, ScreenTitle, Skeleton } from '../ui/kit.tsx';
import { NutritionSummary } from '../ui/NutritionSummary.tsx';
import { Screen } from '../ui/Screen.tsx';
import { CourierAvatar, Glyph, orderTint } from './OrderParts.tsx';
import { TrackingMapCard } from './OrderTrackingMap.tsx';
import { RatingSheet } from './RatingSheet.tsx';

import '../styles/orders.css';

/** Количество к оплате по строке: собранное без возвращённого. */
const soldQty = (item: OrderItem) => (item.pickedQty ?? item.qty) - (item.returnedQty ?? 0);

/** Подарок: скидка покрывает всю сумму строки. */
const isGift = (item: OrderItem) => para(item.priceRsd) > 0n && para(item.discountRsd) >= para(item.priceRsd) * BigInt(item.qty);

const itemName = (item: OrderItem, lang: 'sr' | 'en' | 'ru') => localized(lang, item.name, item.nameEn, item.nameRu);

function statusIcon(status: string): IconName {
  if (OrderStatusGroup.cancelled.has(status)) return 'close';
  if (OrderStatusGroup.awaitingPayment.has(status)) return 'card';
  if (OrderStatusGroup.inWarehouse.has(status)) return 'box';
  if (OrderStatusGroup.onTheWay.has(status)) return 'bike';
  return 'check';
}

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

/**
 * Экран заказа: статус и прогресс, карта с курьером, состав, чек, оплата, отмена, оценка
 * после вручения и переписка с поддержкой.
 *
 * Статус приходит событием ленты и перечитывается с сервера, позиция курьера — событием
 * с координатами.
 */
export function OrderScreen({ orderId }: { orderId: string }) {
  const strings = useStrings();
  const lang = useLang();
  const { orderVersion, ratedOrderId, lastLocation } = useWatcher();
  useStore(paymentConfigStore);
  useStore(savedCardsStore);
  const [method, setMethod] = useState<PaymentMethod>(defaultMethod);
  const [showsCardForm, setShowsCardForm] = useState(false);

  const [order, setOrder] = useState<Order | null>(null);
  const [tracking, setTracking] = useState<OrderTracking | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [showsCancel, setShowsCancel] = useState(false);
  const [showsRating, setShowsRating] = useState(false);

  const stringsRef = useRef(strings);
  stringsRef.current = strings;
  const requestRef = useRef(0);

  /** `keepError` оставляет на экране сообщение действия, после которого заказ перечитан. */
  const reload = useCallback(
    async (keepError = false) => {
      const request = ++requestRef.current;
      try {
        const loaded = await ordersApi.get(orderId);
        if (request !== requestRef.current) return;
        setOrder(loaded);
        if (!keepError) setError(null);

        if (OrderStatusGroup.trackable.has(loaded.status)) {
          const position = await ordersApi.tracking(orderId).catch(() => null);
          if (request === requestRef.current) setTracking(position);
        } else {
          setTracking(null);
        }
      } catch (cause) {
        if (request === requestRef.current) setError(errorText(stringsRef.current, cause));
      }
    },
    [orderId],
  );

  useEffect(() => {
    setOrder(null);
    setTracking(null);
    setError(null);
  }, [orderId]);

  useEffect(() => {
    void reload();
  }, [reload, orderVersion]);

  // Оценка из всплывшего листа: карточка «Оцените заказ» меняется сразу.
  useEffect(() => {
    if (ratedOrderId === orderId) void reload();
  }, [ratedOrderId, orderId, reload]);

  useEffect(() => {
    if (!lastLocation || lastLocation.orderId !== orderId) return;
    setTracking((current) =>
      current ? { ...current, position: { lat: lastLocation.lat, lng: lastLocation.lng, accuracyM: null, at: lastLocation.at } } : current,
    );
  }, [lastLocation, orderId]);

  // Обратный отсчёт до снятия резерва: после него заказ придётся собрать заново.
  const reservedUntil = order?.reservedUntil ?? null;
  useEffect(() => {
    if (!reservedUntil) {
      setSecondsLeft(null);
      return;
    }
    const until = new Date(reservedUntil).getTime();
    const left = () => Math.max(0, Math.floor((until - Date.now()) / 1000));
    setSecondsLeft(left());
    const timer = setInterval(() => {
      const next = left();
      setSecondsLeft(next);
      if (next === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [reservedUntil]);

  /** Повторная оплата с экрана заказа: выбранным способом; новая карта спрашивается в листе. */
  const pay = async () => {
    if (method.kind === 'new') {
      setShowsCardForm(true);
      return;
    }
    setIsWorking(true);
    try {
      if (method.kind === 'bank') {
        const outcome = await goToPaymentPage(orderId);
        if (outcome === 'redirect') return;
        if (outcome === 'declined') setError(strings.errors['payment_declined'] ?? strings.somethingWentWrong);
        await reload(outcome === 'declined');
        return;
      }
      const result = method.kind === 'saved' ? await payWithSavedCard(orderId, method.cardId) : await ordersApi.pay(orderId, { cardToken: 'mock-card' });
      const declined = result.status === 'declined';
      if (declined) setError(declineText(strings, result.reason ?? null));
      await reload(declined);
    } catch (cause) {
      setError(errorText(strings, cause));
    } finally {
      setIsWorking(false);
    }
  };

  const submitCard = async (card: CardInput) => {
    setIsWorking(true);
    try {
      const outcome = await payWithNewCard(orderId, card);
      if (outcome === 'redirect') return;
      setShowsCardForm(false);
      await reload(outcome === 'declined');
    } catch (cause) {
      setShowsCardForm(false);
      setError(errorText(strings, cause));
    } finally {
      setIsWorking(false);
    }
  };

  const cancel = async (reason: string) => {
    setShowsCancel(false);
    setIsWorking(true);
    try {
      await ordersApi.cancel(orderId, reason.trim() || 'client cancelled');
      await reload();
    } catch (cause) {
      setError(errorText(strings, cause));
    } finally {
      setIsWorking(false);
    }
  };

  /** Строка состава знает только идентификатор: товар читается с витрины склада адреса. */
  const openProduct = async (item: OrderItem) => {
    const found = await lookupProduct(item.productId);
    if (found) pushRoute({ name: 'product', item: found });
    else notify(`${itemName(item, lang)}: ${strings.itemGone}`);
  };

  const active = order ? OrderStatusGroup.isActive(order.status) : false;

  return (
    <Screen>
      <div className="readable">
        <ScreenTitle text={strings.checkoutTitle} />
        <div className="gutter stack order-screen">
          {order ? (
            <>
              <StatusCard order={order} />
              {OrderStatusGroup.trackable.has(order.status) && <TrackingMapCard order={order} tracking={tracking} />}
              {order.courierName && (active || order.rating === null) && (
                <CourierCard name={order.courierName} avatarUrl={order.courierAvatarUrl} run={active ? order.run : null} />
              )}
              {order.leaveAtDoor && (
                <Card className="row order-note">
                  <Icon name="door" size={20} />
                  <b>{strings.leaveAtDoorOrder}</b>
                </Card>
              )}
              {order.ageRestricted && active && (
                <Card className="row order-note">
                  <Glyph name="idCard" />
                  <b>{strings.ageRestrictedOrder}</b>
                </Card>
              )}
              {order.deliveryCode && order.requiresDeliveryCode && OrderStatusGroup.onTheWay.has(order.status) && (
                <Card className="stack order-code">
                  <span className="t-footnote t-muted">{strings.deliveryCodeTitle}</span>
                  <b className="t-numeric">{order.deliveryCode}</b>
                  <span className="t-footnote t-muted">{strings.deliveryCodeHint}</span>
                </Card>
              )}
              {OrderStatusGroup.awaitingPayment.has(order.status) && secondsLeft !== null && (
                <div className="row order-reserve" role="status">
                  <Icon name="clock" size={18} />
                  <span className="t-numeric">{secondsLeft > 0 ? strings.reserveLeft(clock(secondsLeft)) : strings.reserveExpired}</span>
                </div>
              )}
              {OrderStatusGroup.delivered.has(order.status) && <RatingCard order={order} onRate={() => setShowsRating(true)} />}
              <ItemsCard order={order} onOpen={(item) => void openProduct(item)} />
              <TotalsCard order={order} />
              {order.receipt?.url && (
                <Card>
                  <a className="row order-receipt" href={order.receipt.url} target="_blank" rel="noreferrer">
                    <Icon name="doc" size={18} />
                    <span className="grow">{strings.receipt}</span>
                    <b>{strings.openReceipt}</b>
                  </a>
                </Card>
              )}

              {order.paidWith && (
                <Card>
                  <div className="row order-paid-with">
                    <BankBadge issuer={order.paidWith.issuer} brand={order.paidWith.brand} />
                    <span className="grow">{strings.paidWithCard}</span>
                    <b className="t-numeric">···· {order.paidWith.last4 ?? '????'}</b>
                  </div>
                </Card>
              )}

              <div className="stack order-actions">
                {OrderStatusGroup.awaitingPayment.has(order.status) && <PaymentPicker method={method} onChange={(next) => { setMethod(next); if (next.kind === 'new') setShowsCardForm(true); }} />}
                {OrderStatusGroup.awaitingPayment.has(order.status) && (
                  <Button busy={isWorking} onClick={() => void pay()}>
                    {strings.payAmount(formatRsd(order.totalRsd))}
                  </Button>
                )}
                {OrderStatusGroup.clientCancellable.has(order.status) ? (
                  <Button kind="secondary" disabled={isWorking} onClick={() => setShowsCancel(true)}>
                    {strings.cancelOrder}
                  </Button>
                ) : (
                  active && (
                    <p className="t-caption t-muted order-cancel-hint">
                      {OrderStatusGroup.onTheWay.has(order.status) ? strings.cancelNeedsOperatorOnTheWay : strings.cancelNeedsOperator}
                    </p>
                  )
                )}
                <button type="button" className="row order-support pressable" onClick={() => pushRoute({ name: 'support', orderId })}>
                  <Icon name="chat" size={20} className="order-support-icon" />
                  <span className="grow">{strings.supportOrder}</span>
                  <Icon name="chevronRight" size={14} weight={2.8} className="order-chevron" />
                </button>
              </div>
            </>
          ) : (
            !error && (
              <>
                <Skeleton height={120} radius={20} />
                <Skeleton height={220} radius={20} />
                <Skeleton height={160} radius={20} />
              </>
            )
          )}

          {error && <ErrorBanner text={error} onRetry={() => void reload()} />}
        </div>
      </div>

      {showsCancel && <CancelDialog onConfirm={(reason) => void cancel(reason)} onCancel={() => setShowsCancel(false)} />}
      {showsRating && order && (
        <RatingSheet
          order={ratingItem(order)}
          onClose={() => {
            setShowsRating(false);
            void reload();
          }}
        />
      )}
      {showsCardForm && order && (
        <Sheet size="auto" locked={isWorking} label={strings.newCard} onClose={() => !isWorking && setShowsCardForm(false)}>
          {(close) => (
            <>
              <div className="sheet-header">
                <button type="button" className="text-button" disabled={isWorking} onClick={close}>
                  {strings.cancel}
                </button>
                <h2>{strings.newCard}</h2>
                <span />
              </div>
              <div className="sheet-body sheet-pad stack">
                <CardForm amountText={formatRsd(order.totalRsd)} busy={isWorking} owner="" onSubmit={(card) => void submitCard(card)} />
              </div>
            </>
          )}
        </Sheet>
      )}
    </Screen>
  );
}

function ratingItem(order: Order): OrderListItem {
  return {
    id: order.id,
    number: order.number,
    status: order.status,
    placedAt: order.placedAt,
    addressText: order.addressText,
    itemsCount: order.items.length,
    totalRsd: order.totalRsd,
    courierName: order.courierName,
    courierAvatarUrl: order.courierAvatarUrl,
    expectedAt: order.expectedAt,
  };
}

function StatusCard({ order }: { order: Order }) {
  const strings = useStrings();
  const lang = useLang();
  const tint = orderTint(order.status);
  const cancelled = OrderStatusGroup.cancelled.has(order.status);
  const stage = OrderStatusGroup.stage(order.status);

  return (
    <Card className="stack order-status">
      <div className="row order-status-head">
        <span className="order-status-icon" style={{ color: tint, background: `color-mix(in srgb, ${tint} 15%, transparent)` }}>
          <Icon name={statusIcon(order.status)} size={22} weight={2.4} />
        </span>
        <div className="grow stack">
          <b className="t-display">{statusText(strings, order.status)}</b>
          <span className="t-footnote t-muted">
            {arrivalText(lang, strings, order.status, order.expectedAt) ?? relativeDay(lang, order.placedAt)}
          </span>
        </div>
      </div>
      {/* Полоса пути заказа: оплата, склад, в пути, вручение. */}
      <div className="order-track" aria-hidden="true">
        {[0, 1, 2, 3].map((index) => (
          <i key={index} style={index <= stage && !cancelled ? { background: tint } : undefined} />
        ))}
      </div>
    </Card>
  );
}

/** Курьер: аватар, имя и роль. В рейсе из нескольких заказов — сколько остановок до нашей. */
function CourierCard({ name, avatarUrl, run }: { name: string; avatarUrl: string | null; run: OrderRunPlace | null }) {
  const strings = useStrings();
  return (
    <Card className="row order-courier">
      <CourierAvatar url={avatarUrl} name={name} size={48} />
      <div className="grow stack">
        <b className="t-display">{name}</b>
        <span className="t-footnote t-muted">{strings.courierName}</span>
        {run && run.size > 1 && (
          <span className="t-footnote order-courier-run">
            {run.ahead > 0 ? strings.courierRunAhead(run.ahead) : strings.courierRunFirst(run.size - 1)}
          </span>
        )}
      </div>
    </Card>
  );
}

/** Оценка: уже поставленные звёзды и чаевые или предложение оценить. */
function RatingCard({ order, onRate }: { order: Order; onRate: () => void }) {
  const strings = useStrings();
  const rating = order.rating;

  if (!rating) {
    return (
      <Card flush>
        <button type="button" className="row order-rate pressable" onClick={onRate}>
          <Icon name="starFill" size={22} className="order-rate-star" />
          <span className="grow stack">
            <b className="t-display">{strings.rateOrderTitle}</b>
            <span className="t-footnote t-muted">{strings.rateOrderHint}</span>
          </span>
          <Icon name="chevronRight" size={14} weight={2.8} className="order-chevron" />
        </button>
      </Card>
    );
  }
  return (
    <Card className="row order-rated">
      <span className="row order-rated-stars" role="img" aria-label={strings.a11yStars(rating.stars)}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Icon key={star} name={star <= rating.stars ? 'starFill' : 'star'} size={18} className={cx(star <= rating.stars && 'is-on')} />
        ))}
      </span>
      <span className="grow" />
      <span className="t-footnote t-muted order-rated-note">
        {para(rating.tipRsd) > 0n ? `${strings.tipsTitle}: ${formatRsd(rating.tipRsd)}` : strings.thanksForRating}
      </span>
    </Card>
  );
}

function ItemsCard({ order, onOpen }: { order: Order; onOpen: (item: OrderItem) => void }) {
  const strings = useStrings();
  const lang = useLang();
  const pickingDone = !OrderStatusGroup.beforePicked.has(order.status);
  // Вес, ккал и БЖУ заказа суммой по строкам в собранном количестве.
  const nutrition = totalNutrition(order.items.map((item) => ({ weightG: item.netWeightG, nutrition: item.nutrition, qty: soldQty(item) })));

  return (
    <Card className="stack order-items">
      <h2 className="t-display">{strings.orderItems}</h2>
      {order.items.map((item) => (
        <button key={item.productId} type="button" className="row order-item pressable" onClick={() => onOpen(item)}>
          <span className="order-item-photo">
            <RemoteImage src={item.photoUrl} />
          </span>
          <span className="grow stack order-item-text">
            <span className="order-item-name">{itemName(item, lang)}</span>
            <span className="t-numeric t-muted order-item-qty">× {soldQty(item)}</span>
            {item.pickedQty !== null && item.pickedQty < item.qty && pickingDone && (
              <b className="t-caption order-item-missing">{strings.itemMissing(item.qty - item.pickedQty)}</b>
            )}
            {item.returnedQty !== null && item.returnedQty > 0 && <b className="t-caption t-muted">{strings.itemReturned(item.returnedQty)}</b>}
          </span>
          {isGift(item) ? (
            <span className="order-gift">{strings.gift}</span>
          ) : (
            <b className="t-numeric order-item-sum">{formatPara(para(item.priceRsd) * BigInt(soldQty(item)))}</b>
          )}
        </button>
      ))}
      <NutritionSummary amount={nutrition} />
    </Card>
  );
}

function TotalsCard({ order }: { order: Order }) {
  const strings = useStrings();
  const lang = useLang();
  const refunded = para(order.refundedRsd);
  const total = para(order.totalRsd);
  const freeDelivery = para(order.deliveryFeeRsd) === 0n;

  return (
    <Card className="stack order-totals">
      <div className="order-total-line is-top">
        <span className="t-muted">{strings.deliveryTo}</span>
        <span className="order-address">{order.addressText}</span>
      </div>
      {para(order.creditUsedRsd) > 0n && (
        <div className="order-total-line">
          <span className="t-muted">{strings.bonusUsed}</span>
          <span className="t-numeric is-accent">−{formatRsd(order.creditUsedRsd)}</span>
        </div>
      )}
      {para(order.discountRsd) > 0n && (
        <div className="order-total-line">
          <span className="t-muted order-promos">
            {order.promotions.map((promo) => localized(lang, promo.title, promo.titleEn, promo.titleRu)).join(', ')}
          </span>
          <span className="t-numeric is-accent">−{formatRsd(order.discountRsd)}</span>
        </div>
      )}
      <div className="order-total-line">
        <span className="t-muted">{strings.deliveryFee}</span>
        <span className={cx('t-numeric', freeDelivery && 'is-accent')}>{freeDelivery ? strings.deliveryFree : formatRsd(order.deliveryFeeRsd)}</span>
      </div>
      <hr className="hairline" />
      {refunded > 0n ? (
        // Часть денег вернулась: исходный итог зачёркнут, возврат выделен, карточку закрывает
        // сумма, которая осталась списанной.
        <>
          <div className="order-total-line">
            <span className="t-muted">{strings.total}</span>
            <s className="t-numeric t-muted">{formatPara(total)}</s>
          </div>
          <div className="order-total-line is-accent order-refund">
            <span className="row">
              <Glyph name="undo" size={16} weight={2.4} />
              <b>{strings.refundedToCard}</b>
            </span>
            <b className="t-numeric">−{formatPara(refunded)}</b>
          </div>
          <div className="order-total-line order-grand">
            <b className="t-display">{strings.chargedTotal}</b>
            <b className="t-numeric">{formatPara(total - refunded)}</b>
          </div>
        </>
      ) : (
        <div className="order-total-line order-grand">
          <b className="t-display">{strings.total}</b>
          <b className="t-numeric">{formatPara(total)}</b>
        </div>
      )}
    </Card>
  );
}

/** Подтверждение отмены с необязательной причиной. */
function CancelDialog({ onConfirm, onCancel }: { onConfirm: (reason: string) => void; onCancel: () => void }) {
  const strings = useStrings();
  const [reason, setReason] = useState('');

  return (
    <div className="dialog-layer" onClick={onCancel}>
      <form
        className="dialog"
        role="alertdialog"
        aria-label={strings.cancelOrderConfirm}
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          onConfirm(reason);
        }}
      >
        <h3>{strings.cancelOrderConfirm}</h3>
        <input
          className="order-cancel-reason"
          autoFocus
          placeholder={strings.cancelReasonPlaceholder}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <div className="dialog-actions">
          <Button small kind="danger" type="submit">
            {strings.confirm}
          </Button>
          <Button small kind="secondary" onClick={onCancel}>
            {strings.cancel}
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Текст отказа по коду эквайера: известные причины из словаря ошибок, остальное — общий отказ. */
function declineText(strings: Strings, reason: string | null): string {
  return (reason && strings.errors[`decline_${reason}`]) || strings.errors['payment_declined'] || strings.somethingWentWrong;
}
